// src/lib/dates.ts — the When filter's presets (Today, This week, Last month, …) and the small date helpers the lettings
// tables share (docs/plans/lettings-one-form.md, phase 2). Every day is a calendar day in Europe/London and a week runs
// Monday to Sunday, the way the API reads `from` / `to`. The frontend sends the preset's dates, never its name.

export const LONDON = 'Europe/London';

export type DatePreset = 'today' | 'yesterday' | 'tomorrow' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'any' | 'custom';
export const DATE_PRESETS: Array<{ key: DatePreset; label: string }> = [
  { key: 'today', label: 'Today' }, { key: 'yesterday', label: 'Yesterday' }, { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'thisWeek', label: 'This week' }, { key: 'lastWeek', label: 'Last week' },
  { key: 'thisMonth', label: 'This month' }, { key: 'lastMonth', label: 'Last month' },
  { key: 'thisYear', label: 'This year' }, { key: 'lastYear', label: 'Last year' },
  { key: 'any', label: 'Any time' }, { key: 'custom', label: 'Custom dates' },
];
export const isDatePreset = (v: unknown): v is DatePreset => DATE_PRESETS.some((p) => p.key === v);
export const presetLabel = (p: string) => DATE_PRESETS.find((x) => x.key === p)?.label ?? 'Today';

/** Today in London, YYYY-MM-DD. */
export const londonToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: LONDON }).format(new Date());

/** A YYYY-MM-DD moved by n days (calendar arithmetic, no time zone involved). */
export function addDays(day: string, n: number) {
  const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const monthEnd = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); // m is 1-based
const pad = (n: number) => String(n).padStart(2, '0');

/** The inclusive London days a preset covers. `any` has no bounds; `custom` takes the from / to given. */
export function presetRange(preset: string, custom?: { from?: string; to?: string }): { from?: string; to?: string } {
  const t = londonToday();
  const [y, m] = t.split('-').map(Number) as [number, number];
  const monday = addDays(t, -((new Date(`${t}T12:00:00Z`).getUTCDay() + 6) % 7));
  switch (preset) {
    case 'yesterday': { const d = addDays(t, -1); return { from: d, to: d }; }
    case 'tomorrow': { const d = addDays(t, 1); return { from: d, to: d }; }
    case 'thisWeek': return { from: monday, to: addDays(monday, 6) };
    case 'lastWeek': return { from: addDays(monday, -7), to: addDays(monday, -1) };
    case 'thisMonth': return { from: `${y}-${pad(m)}-01`, to: monthEnd(y, m) };
    case 'lastMonth': { const ly = m === 1 ? y - 1 : y; const lm = m === 1 ? 12 : m - 1; return { from: `${ly}-${pad(lm)}-01`, to: monthEnd(ly, lm) }; }
    case 'thisYear': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'lastYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'any': return {};
    case 'custom': return { from: custom?.from || undefined, to: custom?.to || undefined };
    default: return { from: t, to: t };
  }
}

/** A London wall-clock time ("2026-10-27", "10:00") as an ISO instant, whatever the browser's own zone. */
export function londonIso(day: string, time: string) {
  const [y, mo, d] = day.split('-').map(Number) as [number, number, number];
  const [h, mi] = time.split(':').map(Number) as [number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  // What London reads at that UTC instant; the difference is London's offset (0 or +1h).
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date(guess)).map((p) => [p.type, p.value]));
  const seen = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
  return new Date(guess - (seen - guess)).toISOString();
}
/** An ISO instant as London's day and hour: { day: 'YYYY-MM-DD', time: 'HH:MM' }. */
export function londonParts(iso: string) {
  const at = new Date(iso);
  return {
    day: new Intl.DateTimeFormat('en-CA', { timeZone: LONDON }).format(at),
    time: at.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  };
}

/** "27 Oct 2026" — a YYYY-MM-DD or an ISO instant (London). */
export const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = v.length === 10 ? new Date(`${v}T12:00:00Z`) : new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: v.length === 10 ? 'UTC' : LONDON });
};
/** "Tue 27 Oct" — a YYYY-MM-DD. */
export const fmtDay = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "Tue 27 Oct, 10:00" — an ISO instant in London. */
export const fmtWhen = (iso: string) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: LONDON });
/** "27 Oct 2026, 14:05" — an ISO instant in London (a consent's time, say). */
export const fmtStamp = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: LONDON });

// ── The When filter's value ([preset, from, to, hourFrom, hourTo]) as a list's query, and where it starts ─────────────
/** "Any time" is a wide window: the lettings lists read a missing from / to as today. */
export const EARLIEST_DAY = '2000-01-01', LATEST_DAY = '2099-12-31';
export function whenQuery(v: string[] | undefined, fallback: DatePreset = 'today') {
  const [preset = fallback, from = '', to = '', hourFrom = '', hourTo = ''] = v ?? [];
  const r = presetRange(isDatePreset(preset) ? preset : fallback, { from, to });
  return { from: r.from ?? EARLIEST_DAY, to: r.to ?? LATEST_DAY, hourFrom: hourFrom || undefined, hourTo: hourTo || undefined };
}
/**
 * Where a table's When starts: `?when=<preset>` from a link (a count tile, say) wins and is not remembered; then
 * `linked` (an older deep link naming a record → Any time); then what this browser remembered under `storageKey`;
 * then `fallback`.
 */
export function initialWhen(storageKey: string, fallback: DatePreset, opts: { url?: string | null; linked?: boolean } = {}): string[] {
  if (opts.url && isDatePreset(opts.url)) return [opts.url];
  if (opts.linked) return ['any'];
  try { const saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as unknown; if (Array.isArray(saved) && isDatePreset(saved[0])) return saved.map(String); } catch { /* private mode */ }
  return [fallback];
}
export function rememberWhen(storageKey: string, v: string[] | undefined, fallback: DatePreset) {
  try { localStorage.setItem(storageKey, JSON.stringify(v?.length ? v : [fallback])); } catch { /* private mode */ }
}
